import { Animation, createAnimation } from '@ionic/react';
import { useCallback, useRef } from 'react';
import { useEvent } from 'react-use';

interface ModalAnimationFromClickResult {
  enterAnimation: (baseEl: HTMLElement) => Animation;
  leaveAnimation: (baseEl: HTMLElement) => Animation;
}

const useModalAnimationFromClick = (): ModalAnimationFromClickResult => {
  const lastClick = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  useEvent('click', (e: MouseEvent) => {
    const x = e.pageX - window.innerWidth / 2;
    const y = window.innerHeight / 2 - e.pageY;
    lastClick.current = { x, y };
  });

  const enterAnimation = useCallback((baseEl: HTMLElement) => {
    const root = baseEl.shadowRoot as ShadowRoot;

    const backdropAnimation = createAnimation()
      .addElement(root.querySelector('ion-backdrop') as Element)
      .fromTo('opacity', '0.1', 'var(--backdrop-opacity)');

    const { x, y } = lastClick.current;

    const wrapperAnimation = createAnimation()
      .addElement(root.querySelector('.modal-wrapper') as Element)
      .fromTo(
        'transform',
        `translateX(${x}px) translateY(${-y}px) scale(0.1)`,
        `translateX(0px) translateY(0px) scale(1)`
      )
      .fromTo('opacity', 0, 1);

    return createAnimation()
      .addElement(baseEl)
      .easing('ease-out')
      .duration(400)
      .addAnimation([backdropAnimation, wrapperAnimation]);
  }, []);

  const leaveAnimation = useCallback((baseEl: HTMLElement) => {
    const root = baseEl.shadowRoot as ShadowRoot;
    const backdropAnimation = createAnimation()
      .addElement(root.querySelector('ion-backdrop') as Element)
      .fromTo('opacity', 'var(--backdrop-opacity)', '0.01');

    const wrapperAnimation = createAnimation()
      .addElement(root.querySelector('.modal-wrapper') as Element)
      .fromTo('transform', `scale(1)`, `scale(0)`)
      .fromTo('opacity', 1, 0);

    return createAnimation()
      .addElement(baseEl)
      .easing('ease-out')
      .duration(200)
      .addAnimation([backdropAnimation, wrapperAnimation]);
  }, []);

  return { enterAnimation, leaveAnimation };
};

export { useModalAnimationFromClick };
